import React from 'react';
import { useTranslation } from 'react-i18next';

import Modal from 'components/Modal/Modal';
import CheckboxInput from 'components/forms/raw/CheckboxInput';
import Markdown from 'components/Markdown/Markdown';
import Button from 'components/Button/Button';
import { useNumberFormatters } from 'hooks/useNumberFormatters';
import Tooltip from 'components/Tooltip/Tooltip';
import { useSelector } from 'store';
import { useSubmitBidsMutation } from 'api';
import Loader from 'components/Loader/Loader';
import LinkButton from 'components/LinkButton/LinkButton';

import { BidSheetRow } from '../../hooks/useBidSheetState';

import useTotals from './hooks/useTotals';
import useSummaryCheckboxes from './hooks/useSummaryCheckboxes';

import './BidsSummaryModal.scss';
import { sortBy } from 'lodash';

export interface BidsSummaryProps {
  onRequestClose: () => void;
  selectedRows: BidSheetRow[];
}

const BidsSummary = ({ selectedRows }: BidsSummaryProps) => {
  const { t } = useTranslation();
  const { id: rfpId } = useSelector((state) => state.rfp.data);
  const { numberValueFormatter, dollarValueFormatter } = useNumberFormatters();

  const [totalDownPayment, totalPayment] = useTotals(selectedRows);
  const { checkboxOneConfig, checkboxTwoConfig } = useSummaryCheckboxes();

  const { mutateAsync, isSuccess, isError, isLoading, error } = useSubmitBidsMutation(rfpId);

  return (
    <div className="enkrateia-bids-summary-modal">
      <h2>{t('pages.bidSheet.bidSummaryModal.subtitle')}</h2>
      <table>
        <thead>
          <tr>
            <th>{t('pages.bidSheet.issuanceYear')}</th>
            <th>{t('pages.bidSheet.volume')}</th>
            <th>{t('pages.bidSheet.downPayment')}</th>
            <th>{t('pages.bidSheet.total')}</th>
            <th>{t('pages.bidSheet.avgPrice')}</th>
          </tr>
        </thead>
        <tbody>
          {sortBy(selectedRows, 'issuanceYear').map((row: BidSheetRow) => (
            <tr key={row.id}>
              <td>{row.issuanceYear}</td>
              <td>{numberValueFormatter(row.volume)}</td>
              <td>{dollarValueFormatter(row.downPayment)}</td>
              <td>{dollarValueFormatter(row.total)}</td>
              <td>{dollarValueFormatter(row.averagePrice)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="summary">
        <div>
          {t('pages.bidSheet.bidSummaryModal.totalDownPayment')} {dollarValueFormatter(totalDownPayment)}
        </div>
        <div>
          {t('pages.bidSheet.bidSummaryModal.totalPayment')} {dollarValueFormatter(totalPayment)}
        </div>
      </div>
      <div className="checkboxes">
        {checkboxOneConfig.visible && (
          <CheckboxInput
            label={<Markdown linkTarget="_blank">{checkboxOneConfig.text}</Markdown>}
            checked={checkboxOneConfig.checked}
            onClick={checkboxOneConfig.toggleChecked}
            disabled={isSuccess || isLoading}
          />
        )}
        {checkboxTwoConfig.visible && (
          <CheckboxInput
            label={<Markdown linkTarget="_blank">{checkboxTwoConfig.text}</Markdown>}
            checked={checkboxTwoConfig.checked}
            onClick={checkboxTwoConfig.toggleChecked}
            disabled={isSuccess || isLoading}
          />
        )}
      </div>
      {isSuccess && (
        <div className="success">
          {t('pages.bidSheet.bidSummaryModal.submitSuccess', { count: selectedRows.length })}
          <LinkButton variant="secondary" to={`/rfp/${rfpId}`}>
            {t('pages.bidSheet.bidSummaryModal.backToDashboard')}
          </LinkButton>
        </div>
      )}
      {isError && (
        <div className="error">
          <p className="error-title">
            {t('pages.bidSheet.bidSummaryModal.submitFailure', { count: selectedRows.length })}
          </p>
          <p>{error?.message}</p>
        </div>
      )}
      {!isSuccess && (
        <div className="submit-button-container">
          <Tooltip
            tooltip={t('pages.bidSheet.bidSummaryModal.pleaseCheckAll')}
            variant="warn"
            disabled={checkboxOneConfig.checked && checkboxTwoConfig.checked}
          >
            <Button
              variant="dark-green-filled"
              disabled={!checkboxOneConfig.checked || !checkboxTwoConfig.checked || isLoading}
              onClick={() => mutateAsync(selectedRows)}
            >
              {!isLoading && t('pages.bidSheet.bidSummaryModal.submitBid', { count: selectedRows.length })}
              {isLoading && <Loader />}
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export interface BidsSummaryModalProps extends BidsSummaryProps {
  isOpen: boolean;
}

const BidsSummaryModal = ({ isOpen, onRequestClose, ...props }: BidsSummaryModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      variant="kliparo"
      title={t('pages.bidSheet.bidSummaryModal.title')}
    >
      {isOpen && <BidsSummary {...props} onRequestClose={onRequestClose} />}
    </Modal>
  );
};

export default BidsSummaryModal;
